import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { PageHeader } from '../components/page';
import { isMissingPracticeRoute } from '../api/hooks';
import type {
  PracticeAgentConnection,
  PracticeAgentFolderState,
  PracticeAgentHeartbeat,
  PracticeAgentJob,
} from '../api/types';
import { formatBytes } from './ClustersPage';

// Shared parts of the Practice computers pages: the computers that run
// practice mode, their jobs, and the app releases and engine bundles they get.

export const UNAVAILABLE = 'This Rudi release has no practice computers yet.';

/** Below this, the self-check counts free disk space as low. */
export const LOW_DISK_BYTES = 5 * 1024 ** 3;

export const control =
  'h-10 rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

const SECTIONS = [
  { to: '/practice-agents', label: 'Computers', exact: true },
  { to: '/practice-agents/overdue', label: 'Overdue exports', exact: false },
  { to: '/practice-agents/releases', label: 'Releases and bundles', exact: false },
] as const;

export function PracticeAgentsHeader({ actions }: { actions?: ReactNode }) {
  const tab = 'rounded-3xl border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent';
  return (
    <>
      <PageHeader
        title="Practice computers"
        description="The computers that run practice mode, the jobs Rudi sends them, and the app releases and engine bundles they install."
        actions={actions}
      />
      <nav aria-label="Practice computers" className="mb-5 flex flex-wrap gap-2">
        {SECTIONS.map(section => (
          <Link
            key={section.to}
            to={section.to}
            className={tab}
            activeOptions={{ exact: section.exact }}
            activeProps={{ className: `${tab} bg-accent font-medium text-accent-foreground` }}
          >
            {section.label}
          </Link>
        ))}
      </nav>
    </>
  );
}

/** Rudi's own errors carry `error`; Cortex's backend and FastAPI use `detail`. */
export function practiceErrorText(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: unknown; detail?: unknown } } })?.response
    ?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;
  return error instanceof Error ? error.message : fallback;
}

/** For QueryState, which shows an Error's message. */
export function practiceLoadError(error: unknown): Error | null {
  if (!error) return null;
  if (isMissingPracticeRoute(error)) return new Error(UNAVAILABLE);
  return new Error(practiceErrorText(error, 'Could not load practice computers'));
}

export function dateText(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : 'Never';
}

export function agoText(value: string | null | undefined, now = Date.now()): string {
  if (!value) return 'never';
  const seconds = Math.max(0, Math.round((now - Date.parse(value)) / 1000));
  if (seconds < 90) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return new Date(value).toLocaleString();
}

export function bytesText(value: number | null | undefined): string {
  return value === null || value === undefined ? 'Unknown' : formatBytes(value);
}

const CONNECTION: Record<
  PracticeAgentConnection,
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  connected: { label: 'Online', variant: 'default' },
  offline: { label: 'Offline', variant: 'secondary' },
  not_connected: { label: 'Not connected', variant: 'outline' },
  revoked: { label: 'Revoked', variant: 'outline' },
};

export function ConnectionBadge({ connection }: { connection: PracticeAgentConnection }) {
  const shown = CONNECTION[connection] ?? { label: connection, variant: 'outline' };
  return <Badge variant={shown.variant}>{shown.label}</Badge>;
}

export const FOLDER_TEXT: Record<PracticeAgentFolderState, string> = {
  ok: 'OK',
  missing: 'missing',
  unwritable: 'not writable',
  unset: 'not set',
};

const SETUP_STEP_TEXT: Record<string, string> = {
  link: 'Link',
  system: 'Practice system',
  folder: 'Clinical Trials folder',
  recovery: 'Recovery key',
  testExport: 'Test export',
  plan: 'Export plan',
  done: 'Done',
};

export function setupStepText(step: string | null | undefined): string {
  return step ? (SETUP_STEP_TEXT[step] ?? step) : 'Unknown';
}

/** What the last self-check found wrong, in short words. Empty when all is fine. */
export function selfCheckProblems(heartbeat: PracticeAgentHeartbeat | null): string[] {
  const check = heartbeat?.selfCheck;
  if (!check) return [];
  const problems: string[] = [];
  if (check.exportFolder !== 'ok')
    problems.push(`Export folder ${FOLDER_TEXT[check.exportFolder] ?? check.exportFolder}`);
  if (check.rudiFolder !== 'ok')
    problems.push(`Rudi folder ${FOLDER_TEXT[check.rudiFolder] ?? check.rudiFolder}`);
  if (!check.hostReachable) problems.push('Rudi host unreachable');
  if (check.patientModelReachable === false) problems.push('Patient model unreachable');
  if (check.localFreeBytes !== null && check.localFreeBytes < LOW_DISK_BYTES)
    problems.push(`Low disk, ${formatBytes(check.localFreeBytes)} free locally`);
  if (check.shareFreeBytes !== null && check.shareFreeBytes < LOW_DISK_BYTES)
    problems.push(`Low disk, ${formatBytes(check.shareFreeBytes)} free on the share`);
  return problems;
}

/** The app updater's state, or null when there is nothing to say. */
export function updateText(heartbeat: PracticeAgentHeartbeat | null): string | null {
  const updates = heartbeat?.updates;
  if (!updates) return null;
  const version = updates.availableVersion ?? 'an update';
  switch (updates.state) {
    case 'idle':
      return null;
    case 'downloading':
      return `Downloading ${version}`;
    case 'verified':
      return `${version} verified, installs when idle`;
    case 'failed':
      return `Update failed${updates.lastError ? ` (${updates.lastError})` : ''}`;
    case 'unsigned':
      return `Update refused, no valid signature${updates.lastError ? ` (${updates.lastError})` : ''}`;
    default:
      return updates.state;
  }
}

export function frozenText(heartbeat: PracticeAgentHeartbeat | null): string[] {
  const frozen = heartbeat?.frozen;
  if (!frozen) return [];
  return [frozen.app && 'App frozen', frozen.bundles && 'Bundles frozen'].filter(
    (text): text is string => !!text
  );
}

export function lastImportText(heartbeat: PracticeAgentHeartbeat | null): string {
  const last = heartbeat?.lastImport;
  if (!last) return 'No import yet';
  const format = last.format ? `, ${last.format.toUpperCase()}` : '';
  return `${last.status === 'succeeded' ? 'Imported' : 'Failed'} ${dateText(last.at)}${format}`;
}

/** A Count from the practice: a number of at least 5, or "lt5" for 0 to 4. */
export function countText(value: unknown): string {
  if (value === 'lt5') return 'fewer than 5';
  return typeof value === 'number' ? value.toLocaleString() : 'unknown';
}

export const JOB_TYPE_TEXT: Record<string, string> = {
  'report.structure': 'Structure report',
  'import.reparse': 'Re-import',
  'prescreen.run': 'Pre-screening run',
};

export const OPEN_JOB_STATUSES = new Set(['queued', 'delivered']);

/** One line about a job's result. Results hold counts, codes and months only. */
export function jobResultText(job: PracticeAgentJob): string {
  if (job.status === 'failed' || job.status === 'rejected')
    return job.errorClass ? `Error ${job.errorClass}` : 'No error class';
  if (job.status === 'expired') return 'Expired before an answer';
  if (job.status === 'cancelled') return 'Cancelled';
  const result = job.result;
  if (!result) return job.status === 'succeeded' ? 'No result' : '';
  if (job.type === 'report.structure') {
    const range = result.dateRange as { from?: string; to?: string } | null | undefined;
    const formats = Array.isArray(result.formats) ? result.formats.join(', ') : '';
    return [
      `${countText(result.patients)} patients`,
      `${countText(result.encounters)} encounters`,
      formats && `formats ${formats}`,
      range?.from && range.to && `${range.from} to ${range.to}`,
    ]
      .filter(Boolean)
      .join(', ');
  }
  if (job.type === 'import.reparse') {
    const errors = Array.isArray(result.parseErrors) ? result.parseErrors.length : 0;
    return `${countText(result.rawExports)} exports, ${countText(result.patients)} patients, ${errors} parse error kinds`;
  }
  return 'Result received';
}

/** A confirmation step before an action that is hard to undo. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  pending,
  destructive = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={next => !next && !pending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={pending}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="mb-8 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function Facts({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <dl
      aria-label={label}
      className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 rounded-3xl border bg-card p-4 text-sm"
    >
      {children}
    </dl>
  );
}
